import { FC, useState, useEffect, useMemo } from "react"
import { useForm } from "react-hook-form"
import { Listing, InventoryItem } from "../../lib/types"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  Input,
  Card,
  CardContent,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  SelectGroup,
  SelectLabel
} from "../../components/ui"
import {
  Package,
  DollarSign,
  FileText,
  CheckCircle2,
  ArrowRight,
  ArrowLeft,
  ExternalLink,
  Search,
  AlertCircle,
  Sparkles
} from "lucide-react"
import { useListingContext } from "../../hooks/use-listings"
import { useInventoryContext } from "../../hooks/use-inventory"
import { Timestamp } from "firebase/firestore"

type NewListingWizardProps = {
  isShowing: boolean
  close: () => void
  listing: Listing | null
}

type ListingFormData = {
  invSourceId: string
  title: string
  quantityListed: number
  price: number
  description?: string
  externalSourceUrl?: string
  platform?: string
}

export const NewListingWizard: FC<NewListingWizardProps> = ({
  isShowing,
  close,
  listing
}) => {
  const { register, getValues, setValue, reset, watch } = useForm<ListingFormData>()
  const { addListing, updateListing } = useListingContext()
  const { inventory } = useInventoryContext()

  const [currentStep, setCurrentStep] = useState(1)
  const [searchTerm, setSearchTerm] = useState("")
  const [selectedInventoryItem, setSelectedInventoryItem] = useState<InventoryItem | null>(null)
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({})

  const watchedQuantity = watch("quantityListed")
  const watchedPrice = watch("price")

  // Filter inventory based on search
  const filteredInventory = useMemo(() => {
    if (!searchTerm) return inventory.filter(item => item.quantity > 0)

    const normalizedQuery = searchTerm.toLowerCase()
    return inventory.filter(item => {
      if (item.quantity <= 0) return false
      const nameMatch = item.name.toLowerCase().includes(normalizedQuery)
      const skuMatch = item.sku?.toLowerCase().includes(normalizedQuery) || false
      return nameMatch || skuMatch
    })
  }, [searchTerm, inventory])

  // Reset form when dialog opens/closes or listing changes
  useEffect(() => {
    if (isShowing) {
      if (listing) {
        // Editing existing listing - skip to step 2 (inventory selection locked)
        const invItem = inventory.find(item => item.id === listing.invSourceId)
        setSelectedInventoryItem(invItem || null)
        setCurrentStep(2)

        reset({
          invSourceId: listing.invSourceId,
          title: listing.title,
          quantityListed: listing.quantityListed,
          price: Number(listing.price) || 0,
          description: listing.description || "",
          externalSourceUrl: listing.externalSourceUrl || "",
          platform: listing.platform || ""
        })
      } else {
        // Creating new listing
        setCurrentStep(1)
        setSelectedInventoryItem(null)
        setSearchTerm("")
        reset({
          invSourceId: "",
          title: "",
          quantityListed: 1,
          price: 0,
          description: "",
          externalSourceUrl: "",
          platform: ""
        })
      }
      setValidationErrors({})
    }
  }, [isShowing, listing, inventory, reset])

  const selectInventoryItem = (item: InventoryItem) => {
    setSelectedInventoryItem(item)
    setValue("invSourceId", item.id)
    setValue("title", item.name)
    setValue("price", Number(item.value))
    setValue("quantityListed", 1)
    setCurrentStep(2)
  }

  const validateStep = (step: number): boolean => {
    const errors: Record<string, string> = {}

    if (step === 1) {
      if (!selectedInventoryItem) {
        errors.invSourceId = "Please select an inventory item"
      }
    }

    if (step === 2) {
      const title = getValues("title")
      const quantity = getValues("quantityListed")
      const price = getValues("price") || 0

      if (!title || title.trim().length === 0) {
        errors.title = "Title is required"
      }

      if (!quantity || quantity < 1) {
        errors.quantityListed = "Quantity must be at least 1"
      }

      if (selectedInventoryItem && quantity > selectedInventoryItem.quantity) {
        errors.quantityListed = `Only ${selectedInventoryItem.quantity} available in inventory`
      }

      if (!price || price < 0) {
        errors.price = "Valid price is required"
      }
    }

    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(currentStep + 1)
    }
  }

  const handleBack = () => {
    setCurrentStep(currentStep - 1)
    setValidationErrors({})
  }

  const handleSubmit = async () => {
    if (!validateStep(currentStep) || !selectedInventoryItem) return

    const formData = getValues()

    if (listing) {
      // Update existing listing
      await updateListing({
        ...listing,
        title: formData.title,
        quantityListed: Number(formData.quantityListed),
        quantityAvailable: Number(formData.quantityListed) - listing.quantitySold,
        price: Number(formData.price),
        description: formData.description,
        externalSourceUrl: formData.externalSourceUrl,
        platform: formData.platform,
        updatedAt: Timestamp.now()
      })
    } else {
      // Create new listing
      const newListing: Partial<Listing> = {
        invSourceId: formData.invSourceId,
        title: formData.title,
        quantityListed: Number(formData.quantityListed),
        quantitySold: 0,
        quantityAvailable: Number(formData.quantityListed),
        price: Number(formData.price),
        description: formData.description,
        externalSourceUrl: formData.externalSourceUrl,
        platform: formData.platform,
        status: 'active',
        createdAt: Timestamp.now()
      }

      await addListing(newListing as Listing)
    }

    close()
  }

  const steps = [
    { number: 1, title: "Select Item", icon: Search },
    { number: 2, title: "Listing Details", icon: FileText },
    { number: 3, title: "Review", icon: CheckCircle2 }
  ]

  const totalValue = (watchedQuantity || 0) * (watchedPrice || 0)

  return (
    <Dialog open={isShowing} onOpenChange={close}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto dark:bg-neutral800 dark:border-neutral700">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-2xl dark:text-neutral100">
            <Sparkles className="text-sumoPrimary" width={28} height={28} />
            {listing ? "Edit Listing" : "Create New Listing"}
          </DialogTitle>
        </DialogHeader>

        {/* Step Progress Indicator */}
        <div className="flex items-start justify-between mb-8 px-2">
          {steps.map((step, index) => {
            const isActive = currentStep === step.number
            const isCompleted = currentStep > step.number
            const Icon = step.icon

            return (
              <>
                <div key={step.number} className="flex flex-col items-center">
                  <div
                    className={`
                      w-12 h-12 rounded-full flex items-center justify-center transition-all duration-200 flex-shrink-0
                      ${isActive ? 'bg-sumoPrimary text-sumoNavy shadow-yellow ring-4 ring-sumoPrimary/20 scale-110' : ''}
                      ${isCompleted ? 'bg-success500 text-white' : ''}
                      ${!isActive && !isCompleted ? 'bg-neutral200 dark:bg-neutral700 text-neutral500 dark:text-neutral400' : ''}
                    `}
                  >
                    <Icon width={20} height={20} />
                  </div>
                  <span className={`mt-1.5 text-[10px] sm:text-xs font-semibold text-center leading-tight max-w-[80px] ${isActive ? 'text-sumoPrimary' : 'text-neutral600 dark:text-neutral400'}`}>
                    {step.title}
                  </span>
                </div>
                {/* Show connector line only on larger screens */}
                {index < steps.length - 1 && (
                  <div className={`hidden sm:block h-1 flex-1 mt-6 mx-2 rounded transition-all duration-300 ${isCompleted ? 'bg-success500' : 'bg-neutral200 dark:bg-neutral700'
                    }`} />
                )}
              </>
            )
          })}
        </div>

        {/* Step Content */}
        <div className="space-y-6">
          {/* Step 1: Select Inventory Item */}
          {currentStep === 1 && (
            <div className="space-y-4 animate-fade-in">
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-semibold text-neutral700 dark:text-neutral300">
                  <Search width={16} height={16} className="text-sumoPrimary" />
                  Search Your Inventory
                </label>
                <Input
                  type="text"
                  placeholder="Search by name or SKU..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="text-lg"
                />
              </div>

              {validationErrors.invSourceId && (
                <p className="text-error500 text-sm flex items-center gap-1">
                  <AlertCircle width={14} /> {validationErrors.invSourceId}
                </p>
              )}

              <div className="grid gap-3 max-h-96 overflow-y-auto">
                {filteredInventory.length === 0 ? (
                  <div className="text-center py-8 text-neutral500 dark:text-neutral400">
                    <Package className="mx-auto mb-2 text-neutral300 dark:text-neutral600" width={48} height={48} />
                    <p>No inventory items found</p>
                    <p className="text-sm">Try adjusting your search</p>
                  </div>
                ) : (
                  filteredInventory.map((item) => (
                    <Card
                      key={item.id}
                      className={`cursor-pointer transition-all hover:border-sumoPrimary hover:shadow-yellow dark:bg-neutral800 dark:border-neutral700 ${selectedInventoryItem?.id === item.id ? 'border-sumoPrimary bg-sumoCream/30 dark:bg-sumoPrimary/20' : ''
                        }`}
                      onClick={() => selectInventoryItem(item)}
                    >
                      <CardContent className="pt-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-semibold text-neutral900 dark:text-neutral100">{item.name}</h4>
                            {item.sku && (
                              <p className="text-sm text-neutral500 dark:text-neutral400">SKU: {item.sku}</p>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-neutral600 dark:text-neutral400">Available</p>
                            <p className="text-lg font-bold text-sumoPrimary">{item.quantity}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Step 2: Listing Details */}
          {currentStep === 2 && (
            <div className="space-y-4 animate-fade-in">
              {selectedInventoryItem && (
                <Card className="bg-sumoCream/30 dark:bg-sumoPrimary/10 border-sumoPrimary/30 dark:border-sumoPrimary/40">
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2">
                      <Package width={18} height={18} className="text-sumoPrimary" />
                      <span className="font-semibold text-neutral900 dark:text-neutral100">{selectedInventoryItem.name}</span>
                      <span className="text-sm text-neutral500 dark:text-neutral400">
                        ({selectedInventoryItem.quantity} available)
                      </span>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Title */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-semibold text-neutral700 dark:text-neutral300">
                  <FileText width={16} height={16} className="text-sumoPrimary" />
                  Listing Title *
                </label>
                <Input
                  type="text"
                  placeholder="Enter listing title"
                  {...register("title", { required: true })}
                  className={validationErrors.title ? 'border-error500' : ''}
                />
                {validationErrors.title && (
                  <p className="text-error500 text-xs flex items-center gap-1">
                    <AlertCircle width={12} /> {validationErrors.title}
                  </p>
                )}
              </div>

              {/* Quantity and Price */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-semibold text-neutral700 dark:text-neutral300">
                    <Package width={16} height={16} className="text-sumoPrimary" />
                    Quantity *
                  </label>
                  <Input
                    type="number"
                    min="1"
                    max={selectedInventoryItem?.quantity || 999}
                    placeholder="1"
                    {...register("quantityListed", { required: true })}
                    className={validationErrors.quantityListed ? 'border-error500' : ''}
                  />
                  {validationErrors.quantityListed && (
                    <p className="text-error500 text-xs flex items-center gap-1">
                      <AlertCircle width={12} /> {validationErrors.quantityListed}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-semibold text-neutral700 dark:text-neutral300">
                    <DollarSign width={16} height={16} className="text-sumoPrimary" />
                    Price per Unit *
                  </label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    {...register("price", { required: true })}
                    className={validationErrors.price ? 'border-error500' : ''}
                  />
                  {validationErrors.price && (
                    <p className="text-error500 text-xs flex items-center gap-1">
                      <AlertCircle width={12} /> {validationErrors.price}
                    </p>
                  )}
                </div>
              </div>

              {/* Total Value Display */}
              {totalValue > 0 && (
                <Card className="bg-sumoCream dark:bg-sumoPrimary/10 border-sumoPrimary/50 dark:border-sumoPrimary/40">
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-neutral700 dark:text-neutral300">
                        Total Listing Value
                      </span>
                      <span className="text-2xl font-bold text-sumoPrimary">
                        ${totalValue.toFixed(2)}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Platform */}
              <div className="space-y-2">
                <label className="text-sm font-semibold text-neutral700 dark:text-neutral300">
                  Platform <span className="text-neutral400 dark:text-neutral500 font-normal text-xs">(Optional)</span>
                </label>
                <Select
                  value={getValues("platform")}
                  onValueChange={(value) => setValue("platform", value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select platform" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectLabel>Sales Platforms</SelectLabel>
                      <SelectItem value="eBay">eBay</SelectItem>
                      <SelectItem value="Facebook Marketplace">Facebook Marketplace</SelectItem>
                      <SelectItem value="Craigslist">Craigslist</SelectItem>
                      <SelectItem value="Amazon">Amazon</SelectItem>
                      <SelectItem value="Etsy">Etsy</SelectItem>
                      <SelectItem value="Shopify">Shopify</SelectItem>
                      <SelectItem value="Mercari">Mercari</SelectItem>
                      <SelectItem value="Poshmark">Poshmark</SelectItem>
                      <SelectItem value="In Person">In Person</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>

              {/* External URL */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-semibold text-neutral700 dark:text-neutral300">
                  <ExternalLink width={16} height={16} className="text-sumoPrimary" />
                  External Listing URL <span className="text-neutral400 dark:text-neutral500 font-normal text-xs">(Optional)</span>
                </label>
                <Input
                  type="url"
                  placeholder="https://..."
                  {...register("externalSourceUrl")}
                />
              </div>

              {/* Description */}
              <div className="space-y-2">
                <label className="text-sm font-semibold text-neutral700 dark:text-neutral300">
                  Description <span className="text-neutral400 dark:text-neutral500 font-normal text-xs">(Optional)</span>
                </label>
                <textarea
                  placeholder="Add any additional details about this listing..."
                  {...register("description")}
                  className="w-full min-h-24 px-3 py-2 border-2 border-neutral300 dark:border-neutral600 bg-white dark:bg-neutral800 text-neutral900 dark:text-neutral100 placeholder:text-neutral400 dark:placeholder:text-neutral500 rounded-md focus:outline-none focus:border-sumoPrimary focus:ring-4 focus:ring-sumoPrimary/10"
                />
              </div>
            </div>
          )}

          {/* Step 3: Review */}
          {currentStep === 3 && (
            <div className="space-y-4 animate-fade-in">
              <Card className="bg-gradient-to-br from-sumoCream to-white dark:from-neutral800 dark:to-neutral800 border-sumoPrimary dark:border-sumoPrimary/50 shadow-yellow">
                <CardContent className="pt-6 space-y-4">
                  <div className="flex items-center gap-2 mb-4">
                    <CheckCircle2 className="text-success500" width={24} height={24} />
                    <h3 className="text-lg font-bold text-neutral900 dark:text-neutral100">Review Your Listing</h3>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-neutral600 dark:text-neutral400 mb-1">Item Source</p>
                      <p className="font-semibold text-neutral900 dark:text-neutral100">{selectedInventoryItem?.name}</p>
                    </div>
                    <div>
                      <p className="text-xs text-neutral600 dark:text-neutral400 mb-1">SKU</p>
                      <p className="font-semibold text-neutral900 dark:text-neutral100">{selectedInventoryItem?.sku || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-neutral600 dark:text-neutral400 mb-1">Listing Title</p>
                      <p className="font-semibold text-neutral900 dark:text-neutral100">{getValues("title")}</p>
                    </div>
                    <div>
                      <p className="text-xs text-neutral600 dark:text-neutral400 mb-1">Platform</p>
                      <p className="font-semibold text-neutral900 dark:text-neutral100">{getValues("platform") || 'Not specified'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-neutral600 dark:text-neutral400 mb-1">Quantity</p>
                      <p className="font-semibold text-sumoPrimary text-xl">{getValues("quantityListed")}</p>
                    </div>
                    <div>
                      <p className="text-xs text-neutral600 dark:text-neutral400 mb-1">Price per Unit</p>
                      <p className="font-semibold text-sumoPrimary text-xl">${Number(getValues("price") || 0)?.toFixed(2)}</p>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-neutral200 dark:border-neutral700">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-neutral700 dark:text-neutral300">Total Listing Value</span>
                      <span className="text-3xl font-bold text-sumoPrimary">${totalValue.toFixed(2)}</span>
                    </div>
                  </div>

                  {getValues("description") && (
                    <div className="pt-4 border-t border-neutral200 dark:border-neutral700">
                      <p className="text-xs text-neutral600 dark:text-neutral400 mb-1">Description</p>
                      <p className="text-sm text-neutral700 dark:text-neutral300">{getValues("description")}</p>
                    </div>
                  )}

                  {getValues("externalSourceUrl") && (
                    <div className="pt-4 border-t border-neutral200 dark:border-neutral700">
                      <p className="text-xs text-neutral600 dark:text-neutral400 mb-1">External URL</p>
                      <a
                        href={getValues("externalSourceUrl")}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-blue600 hover:underline flex items-center gap-1"
                      >
                        {getValues("externalSourceUrl")}
                        <ExternalLink width={12} height={12} />
                      </a>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </div>

        {/* Footer Navigation */}
        <DialogFooter className="flex items-center justify-between gap-2">
          <div className="flex gap-2">
            {currentStep > 1 && !listing && (
              <Button variant="outline" onClick={handleBack}>
                <ArrowLeft width={16} height={16} />
                Back
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={close}>
              Cancel
            </Button>
            {currentStep < 3 ? (
              <Button variant="primary" onClick={handleNext}>
                Next
                <ArrowRight width={16} height={16} />
              </Button>
            ) : (
              <Button variant="primary" onClick={handleSubmit}>
                <CheckCircle2 width={16} height={16} />
                {listing ? "Update Listing" : "Create Listing"}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
